/**
 * AdoraCare -- THE REGISTRATION FIELD PLAN
 * (REGISTRATION-CONTRACT-AND-WALK-CLOSURE-V1, D1/D2/D3/D6).
 *
 * The ONE pure derivation that turns the TENANT REQUIRED-FIELD CONFIG into
 * the ordered set of controls the registration form renders. No IO, no
 * React -- the component renders EXACTLY this plan, and the completability
 * test reads EXACTLY this plan, so "the config says required" and "a control
 * exists" can never drift apart again.
 *
 * WHY THIS EXISTS (the walk defect it closes): `city` was in the seeded
 * tenant required set (DEFAULT_REGISTRATION_REQUIRED_FIELDS) and in the
 * server's completeness derivation (missingRegistrationFields), and the form
 * carried a `city` state variable that it POSTED -- but it rendered NO
 * control for it. The desk could never satisfy the contract from the
 * registration form: submit returned registration_capture_required naming a
 * field the form did not ask for. One config truth, one render truth, one
 * validator truth -- the COMPLETABILITY-INVARIANT.
 *
 * CANONICAL KEYS ONLY (KEY-STABILITY): every key below is the EXISTING token
 * from lib/domain/registration/registration-config.ts. This module renames
 * NOTHING. A key that is semantically wrong is recorded as a PARKED migration
 * candidate in docs/design/registration/REGISTRATION-CONTRACT.md and changed
 * by its own key-migration package (config + UI + backend + data together),
 * never inside a feature package.
 *
 * DESK VOICE (D3): the labels here are the ones a receptionist says out loud
 * ("City", "Phone"), never an i18n key, a schema key, or a snake_case token.
 * NO-RAW-KEYS is CI-enforced -- nothing in REGISTRATION_DESK_LABELS may be a
 * bare canonical key.
 */

/**
 * The registration groups, in RENDER ORDER (D6). Identity first because it is
 * what the desk confirms verbally with the person in front of them; the
 * reason chips last because they close the conversation.
 */
export const REGISTRATION_GROUP_ORDER = [
  'identity',
  // REGISTRATION-PARTHA-CONTRACT-V1 (Part C): the alternate-number owner
  // detail groups here, after identity and before family.
  'contact',
  'family',
  'address',
  'other',
  // REGISTRATION-PARTHA-CONTRACT-V1 (Part D): the emergency-contact trio is
  // its own section, after the other details and before the reason.
  'emergency',
  'reason',
] as const;

export type RegistrationGroup = (typeof REGISTRATION_GROUP_ORDER)[number];

export const REGISTRATION_GROUP_LABELS: Readonly<
  Record<RegistrationGroup, string>
> = Object.freeze({
  identity: 'Identity',
  contact: 'Contact',
  family: 'Family',
  address: 'Address',
  other: 'Other details',
  emergency: 'Emergency contact',
  reason: 'Reason for visit',
});

/**
 * How a field's ONE control is drawn. The plan carries the SHAPE, the
 * component owns the pixels -- so a control kind can never be invented at the
 * render site without appearing here first.
 */
export type RegistrationControlKind =
  | 'identity_row' // rendered inside the details card (name)
  | 'phone' // the details card's phone cell (D4: reveal-prefilled)
  | 'age_or_dob' // the existing Age|DOB segmented toggle
  | 'chips' // a chip row (gender, handling category, reason)
  | 'select' // a dropdown picklist (occupation, 8.6 RULING 3)
  | 'text' // a plain single-line input
  | 'email'; // an email input (type=email keyboard; G2, PDC-FO-GAP-CLOSURE-V1)

export interface RegistrationFieldSpec {
  /** The CANONICAL backend key -- never renamed here (KEY-STABILITY). */
  readonly key: string;
  readonly group: RegistrationGroup;
  /** Desk voice. Never a raw key (NO-RAW-KEYS). */
  readonly label: string;
  readonly control: RegistrationControlKind;
  /** TRUE when the ACTIVE tenant config makes this field mandatory. */
  readonly required: boolean;
  /** TRUE when the control sits behind the "Add full address" expander
   *  (contract 2.4). A REQUIRED field is NEVER an expander resident --
   *  the plan promotes it to the visible Address group, because a control
   *  behind a collapsed expander is not the "one visible, enabled
   *  control" the COMPLETABILITY-INVARIANT demands. */
  readonly expander: boolean;
}

/**
 * Desk-voice labels for every canonical registration key. These are the SAME
 * words the needs-line uses, so the person reading "Registration still needs:
 * City" finds a control labelled "City" -- one vocabulary, one surface.
 *
 * PARITY NOTE: lib/domain/registration/lead-registration.ts
 * REGISTRATION_FIELD_LABELS is the SERVER's label home for the same keys. The
 * two are pinned equal by the completability test rather than merged, because
 * the server list is consumed by server-rendered warnings and this one by the
 * form; a divergence is a test failure, not a silent drift.
 */
export const REGISTRATION_DESK_LABELS: Readonly<Record<string, string>> =
  Object.freeze({
    full_name: 'Name',
    mobile: 'Phone',
    age_or_dob: 'Age or date of birth',
    gender: 'Gender',
    city: 'City',
    relation_name: 'Relation name',
    occupation: 'Occupation',
    handling_category: 'Visit category',
    reason_for_visit: 'Reason for visit',
    external_mr: 'Old MR No',
    // REGISTRATION-CONTRACT-COMPLETE-PREP-V1: the 20260808_1 contract
    // fields (contract 2.2/2.3/2.4), desk voice.
    name_title: 'Title',
    last_name: 'Last name',
    relation_title: 'Relation title',
    state: 'State',
    pincode: 'Pincode',
    address_door: 'Door no / building',
    address_street: 'Street',
    address_area: 'Area / town / village',
    address_landmark: 'Landmark',
    address_taluka: 'Taluka',
    address_country: 'Country',
    // REGISTRATION-PARTHA-CONTRACT-V1 (Part C/D): the alt-number owner detail
    // + the emergency-contact trio, desk voice. NOTE: 'Alternate phone' (not
    // 'mobile') keeps the desk copy clear of the raw canonical key `mobile`
    // (NO-RAW-KEYS), the same reason the primary field's label is 'Phone'.
    alt_mobile: 'Alternate phone',
    alt_mobile_relation: 'Alternate phone relation',
    alt_mobile_relation_name: 'Alternate phone relation name',
    emergency_contact_name: 'Emergency contact name',
    emergency_contact_relation: 'Emergency contact relation',
    emergency_contact_phone: 'Emergency contact phone',
    // PDC-FO-GAP-CLOSURE-V1 (G2, founder ruling 2026-08-14): the optional
    // registration email, desk voice.
    email: 'Email',
  });

/** Which group each canonical key belongs to (D6 group order). */
const FIELD_GROUP: Readonly<Record<string, RegistrationGroup>> = Object.freeze({
  full_name: 'identity',
  mobile: 'identity',
  age_or_dob: 'identity',
  gender: 'identity',
  name_title: 'identity',
  last_name: 'identity',
  relation_name: 'family',
  relation_title: 'family',
  city: 'address',
  state: 'address',
  pincode: 'address',
  address_door: 'address',
  address_street: 'address',
  address_area: 'address',
  address_landmark: 'address',
  address_taluka: 'address',
  address_country: 'address',
  occupation: 'other',
  handling_category: 'other',
  external_mr: 'other',
  // REGISTRATION-PARTHA-CONTRACT-V1 (Part C/D).
  alt_mobile: 'contact',
  alt_mobile_relation: 'contact',
  alt_mobile_relation_name: 'contact',
  // PDC-FO-GAP-CLOSURE-V1 (G2): email is a CONTACT channel, so it sits in
  // the Contact group beside the alternate phone (the contract's 2.3 row
  // predates this group's existence; the contract row moves with this
  // package -- contract + mockup + implementation together).
  email: 'contact',
  emergency_contact_name: 'emergency',
  emergency_contact_relation: 'emergency',
  emergency_contact_phone: 'emergency',
  reason_for_visit: 'reason',
});

/** Which control each canonical key draws. */
const FIELD_CONTROL: Readonly<Record<string, RegistrationControlKind>> =
  Object.freeze({
    full_name: 'identity_row',
    mobile: 'phone',
    age_or_dob: 'age_or_dob',
    gender: 'chips',
    name_title: 'select',
    last_name: 'text',
    relation_name: 'text',
    relation_title: 'select',
    city: 'text',
    state: 'text',
    pincode: 'text',
    address_door: 'text',
    address_street: 'text',
    address_area: 'text',
    address_landmark: 'text',
    address_taluka: 'text',
    address_country: 'text',
    occupation: 'text',
    handling_category: 'chips',
    external_mr: 'text',
    // REGISTRATION-PARTHA-CONTRACT-V1 (Part C/D): phones + names are text
    // inputs; the two relations are picklists over the banked relation
    // vocabulary (served over the prefill wire, like occupation/title).
    alt_mobile: 'text',
    alt_mobile_relation: 'select',
    alt_mobile_relation_name: 'text',
    emergency_contact_name: 'text',
    emergency_contact_relation: 'select',
    emergency_contact_phone: 'text',
    // PDC-FO-GAP-CLOSURE-V1 (G2): the email keyboard on the tablet door.
    email: 'email',
    reason_for_visit: 'chips',
  });

/**
 * The fields that ALWAYS render, whether or not the tenant requires them.
 * Identity is the desk's job at every arrival, and the reason chips are the
 * founder-kept single row (the REQUIRED SEED note). Everything else appears
 * only when the tenant contract asks for it -- the C16 fail-closed precedent:
 * a field with no config row and no storage owner renders NOWHERE.
 */
const ALWAYS_RENDERED: readonly string[] = Object.freeze([
  'full_name',
  'mobile',
  'age_or_dob',
  'gender',
  'reason_for_visit',
]);

/**
 * Config-gated fields whose columns exist TODAY (migration 20260803_1 /
 * 20260804_1 applied): they render when the tenant contract names them.
 * `handling_category` additionally needs a non-empty configured vocabulary --
 * requiring a tag with no categories to pick would be an unsatisfiable
 * contract, so the plan drops it and the completability test reads the same
 * rule (one derivation, not two opinions).
 */
const CONFIG_GATED: readonly string[] = Object.freeze([
  'relation_name',
  'city',
  'occupation',
  'handling_category',
  // REGISTRATION-FIELD-CONTROLS-V1 (founder walk order 2026-08-13 evening):
  // Full name is the ONE receptionist-facing patient-name entry. last_name
  // moves out of the always-rendered COMPACT_OPTIONAL set because the
  // consumer audit proved it load-bearing NOWHERE -- no search, identity
  // ladder, duplicate matching, UMR, report, or integration reads it, the
  // patients-plane copy is write-only, and no Partha/EHNote requirements
  // doc names a Last-name field. Storage, writers, wire fields, prefill
  // round-trip, and the requirable token all STAND (a tenant row naming
  // last_name renders the control again; a stored legacy value still rides
  // the desk submit invisibly) -- the UI just stops asking the desk to
  // type the same name fact twice.
  'last_name',
  // REGISTRATION-CONTRACT-COMPLETE-PREP-V1: the relation-title half of the
  // 8.1 split takes the SAME C16 posture as its relation_name sibling --
  // it renders when the tenant contract names it (org 1's Studio row gains
  // the token in the founder's post-apply step; the seeded default already
  // carries it for row-less orgs).
  'relation_title',
  // REGISTRATION-PARTHA-CONTRACT-V1 (Part C/D): the alt-number owner detail +
  // the emergency-contact trio take the SAME C16 fail-closed posture -- they
  // render only when the tenant contract names them (org 1's seed row does;
  // a row-less org collects no extra PHI). Columns: migration 20260812_1.
  'alt_mobile',
  'alt_mobile_relation',
  'alt_mobile_relation_name',
  'emergency_contact_name',
  'emergency_contact_relation',
  'emergency_contact_phone',
]);

/**
 * The COMPACT OPTIONAL set (contract 2.3: "visible, never mandatory") --
 * rendered ALWAYS now that 20260808_1 gives each a column and the writer
 * persists it; requiredness still follows the config (name_title is in
 * the founder-interim required seed per 8.6 RULING 2). alt_mobile lives in
 * CONFIG_GATED (its vault posture superseded by the founder's 2026-08-12 Q1
 * PLAINTEXT-FENCED ruling; history: the earlier note here read "alt_mobile /
 * email are DELIBERATELY ABSENT -- vault posture, no lawful plaintext
 * writer, contract 8.1"). EMAIL joins THIS set by the
 * PDC-FO-GAP-CLOSURE-V1 G2 founder ruling (2026-08-14): optional, visible,
 * NEVER requirable (R-A/FS-12 -- the requirable vocabulary still excludes
 * it), validated by THE ONE parseContractEmail, written plaintext-fenced on
 * the lead plane and golden-record-bound at mint. blood_group stays
 * DELIBERATELY ABSENT (founder OUT list, 8.2/8.5 -- the column stays
 * inert); last_name MOVED to CONFIG_GATED by REGISTRATION-FIELD-CONTROLS-V1
 * (see that list's note -- the one-name-entry rule).
 */
const COMPACT_OPTIONAL: readonly string[] = Object.freeze([
  'name_title',
  'state',
  'pincode',
  'email',
]);

/**
 * SHIPPED-WORK-TRUTH-CORRECTIVE-V1 (T4, founder ruling 2026-08-15) -- the
 * EMAIL POSTURE LINE, in the desk voice: the operator sees WHERE the email
 * goes. One copy home; both the field hint and any pin read THIS constant.
 * True on every door: the lead-plane writers keep it with the registration
 * and the mint lands it on the patient record; the person-only create lands
 * it directly.
 */
export const REGISTRATION_EMAIL_POSTURE_LINE =
  'Optional. Kept with this registration and saved to the patient record.';

/**
 * T4 save-then-reopen truth: when a saved registration already carries an
 * email, the reopened door SAYS SO instead of presenting a silently blank
 * field. PRESENCE ONLY -- the stored address itself never re-rides the
 * staff wire (the D4 contact-channel posture stands; the same
 * fingerprint-presence idiom as the phone field).
 */
export const REGISTRATION_EMAIL_ON_FILE_LINE =
  'An email is on file for this registration. Enter a new one only to replace it.';

/**
 * BEHIND "Add full address" (contract 2.4) -- rendered always, but inside
 * the collapsed expander unless the tenant config REQUIRES the field, in
 * which case it is promoted to the visible Address group (the
 * COMPLETABILITY-INVARIANT rule stated on the spec's `expander` flag).
 * `address_area` is required in the founder-interim seed (8.1: YES), so
 * it renders visible under the seeded default.
 */
const ADDRESS_EXPANDER: readonly string[] = Object.freeze([
  'address_area',
  'address_door',
  'address_street',
  'address_landmark',
  'address_taluka',
  'address_country',
]);

export interface RegistrationPlanFacts {
  /** The tenant's `registration.required_fields` tokens (the wire). */
  readonly requiredFields: readonly string[];
  /** The sibling `registration.gender_required` rule (one owner per rule). */
  readonly genderRequired: boolean;
  /** The org's configured handling-category vocabulary ([] = render nowhere). */
  readonly handlingCategories: readonly string[];
  /** The org's occupation picklist vocabulary (8.6 RULING 3). [] = the
   *  control DEGRADES to free text rather than disappearing -- occupation
   *  is a REQUIRABLE field, so an empty vocabulary must never make a
   *  required contract unsatisfiable (contrast handling_category, whose
   *  empty vocabulary lawfully drops the tag). */
  readonly occupationOptions: readonly string[];
  /** The Title picklist vocabulary (contract 2.3 + RULING 2). [] = the
   *  same free-text degrade occupation takes (R-017). Optional so older
   *  callers/fixtures keep compiling (absent = degrade). */
  readonly nameTitleOptions?: readonly string[];
  /** The Relation Title picklist vocabulary (contract 2.2/8.1). [] /
   *  absent = free-text degrade (R-017). */
  readonly relationTitleOptions?: readonly string[];
}

/**
 * THE PLAN. Ordered by group, then by the declared order within each group.
 * Exactly ONE entry per canonical key -- duplicates are structurally
 * impossible because the plan is built by walking a key list, not by
 * accumulating render sites.
 */
export function registrationFieldPlan(
  facts: RegistrationPlanFacts,
): RegistrationFieldSpec[] {
  const required = new Set(facts.requiredFields);
  const isRequired = (key: string): boolean =>
    key === 'gender'
      ? required.has('gender') || facts.genderRequired
      : required.has(key);

  const rendered: string[] = [];
  for (const key of ALWAYS_RENDERED) rendered.push(key);
  for (const key of COMPACT_OPTIONAL) rendered.push(key);
  for (const key of CONFIG_GATED) {
    if (!required.has(key)) continue;
    if (key === 'handling_category' && facts.handlingCategories.length === 0) {
      continue;
    }
    rendered.push(key);
  }
  for (const key of ADDRESS_EXPANDER) rendered.push(key);

  // The three picklists degrade to free text on an EMPTY configured
  // vocabulary (R-017: a REQUIRED field stays completable in every config
  // state -- the RULING 3 occupation precedent, applied identically to the
  // two 20260808_1 title picklists).
  const selectVocabulary = (key: string): readonly string[] | null =>
    key === 'occupation'
      ? facts.occupationOptions
      : key === 'name_title'
        ? (facts.nameTitleOptions ?? [])
        : key === 'relation_title'
          ? (facts.relationTitleOptions ?? [])
          : null;

  const specs = rendered.map((key) => {
    const vocabulary = selectVocabulary(key);
    return {
      key,
      group: FIELD_GROUP[key] ?? ('other' as RegistrationGroup),
      label: REGISTRATION_DESK_LABELS[key] ?? key,
      control:
        vocabulary !== null
          ? vocabulary.length > 0
            ? ('select' as RegistrationControlKind)
            : ('text' as RegistrationControlKind)
          : (FIELD_CONTROL[key] ?? ('text' as RegistrationControlKind)),
      // Contract 2.4: an expander resident stays behind "Add full address"
      // ONLY while optional; a config-required member is promoted to the
      // visible Address group (COMPLETABILITY-INVARIANT -- see the spec
      // field's own comment).
      expander: ADDRESS_EXPANDER.includes(key) && !isRequired(key),
      required: isRequired(key),
    };
  });

  return specs.sort(
    (a, b) =>
      REGISTRATION_GROUP_ORDER.indexOf(a.group) -
      REGISTRATION_GROUP_ORDER.indexOf(b.group),
  );
}

/**
 * The keys the plan renders as a control the desk can actually fill. The
 * completability test compares this against the ACTIVE config: every required
 * key must appear here exactly once.
 */
export function plannedFieldKeys(
  facts: RegistrationPlanFacts,
): readonly string[] {
  return registrationFieldPlan(facts).map((s) => s.key);
}

/**
 * The canonical key a required token maps to when the tenant requires a field
 * this build cannot yet render (no column, no writer). Returns the tokens that
 * are required by config but ABSENT from the plan -- an UNSATISFIABLE
 * CONTRACT, which the completability test fails on rather than shipping a
 * form the desk can never complete.
 */
export function unsatisfiableRequiredKeys(
  facts: RegistrationPlanFacts,
): readonly string[] {
  const planned = new Set(plannedFieldKeys(facts));
  const out: string[] = [];
  for (const key of facts.requiredFields) {
    if (!planned.has(key)) out.push(key);
  }
  if (facts.genderRequired && !planned.has('gender')) out.push('gender');
  return out;
}

// --- D3: THE ONE VALIDATION SURFACE -----------------------------------------

/**
 * THE STICKY NEEDS-LINE -- the form's ONLY user-facing validation surface
 * (ONE-VALIDATION-PATH). It is COMPACT (REGISTRATION-DEPLOYED-WALK-CLOSURE-V1,
 * Defect B3, founder walk 2026-08-13): field-level required marks are the
 * PRIMARY truth, so the bottom line never restates a giant comma-list of every
 * missing field. It names the FIRST unresolved field (the focus anchor -- the
 * same desk voice the field's own label uses) and counts the rest:
 *
 *     0 missing   -> null (silence, never an empty state)
 *     1 missing   -> "Registration still needs: Emergency phone."   (the mockup)
 *     >1 missing  -> "Registration still needs: City, and 5 more."  (compact)
 *
 * ONE TRUTH: the count and the named field derive from the SAME missingKeys the
 * fields and the submit button read -- no second completeness calculation.
 *
 * The retired surface it replaces: the raw server ValidationError message
 * rendered verbatim into the submit bar. That string named a WIRE PATH -- the
 * dotted `fields.<camelCaseKey>` shape -- not a field the desk could see, for
 * a field the form already held: the exact NO-RAW-KEYS defect class. The guard
 * tests/unit/registration-contract-guard.test.ts fails the build if such a
 * path reappears in copy on any registration surface (including this file,
 * which is why the shape is described here rather than quoted).
 */
export function registrationNeedsLine(
  missingKeys: readonly string[],
): string | null {
  if (missingKeys.length === 0) return null;
  const first = REGISTRATION_DESK_LABELS[missingKeys[0]] ?? missingKeys[0];
  if (missingKeys.length === 1) {
    return `Registration still needs: ${first}.`;
  }
  return `Registration still needs: ${first}, and ${missingKeys.length - 1} more.`;
}

/**
 * Map the server's missing-field report back onto the needs-line vocabulary.
 * The server may answer with canonical TOKENS (`city`) or with its own human
 * labels (`City`) depending on the path; both resolve to the SAME desk
 * sentence, so the desk never sees two dialects of "what is missing".
 */
export function needsLineFromServer(
  missing: readonly string[],
): string | null {
  if (missing.length === 0) return null;
  const labelFor = (entry: string): string => {
    const direct = REGISTRATION_DESK_LABELS[entry];
    if (direct !== undefined) return direct;
    const normalized = entry.trim().toLowerCase().replace(/\s+/g, '_');
    return REGISTRATION_DESK_LABELS[normalized] ?? entry.trim();
  };
  // Same COMPACT grammar as registrationNeedsLine (Defect B3): name the first,
  // count the rest -- the desk never reads a giant comma-list, whether the gap
  // was caught on the client or reported by the server's contract check.
  const first = labelFor(missing[0]);
  if (missing.length === 1) {
    return `Registration still needs: ${first}.`;
  }
  return `Registration still needs: ${first}, and ${missing.length - 1} more.`;
}
